import React, { useMemo } from 'react';

import { Control, Controller, useController, useWatch } from 'react-hook-form';
import dayjs from '~/timeInit';

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import tenants from '~/consts/tenant';

import { formatDateLongISO } from '~/services/TimeService';

const SELECT_ALL_VALUE = '__SELECT_ALL__';

export interface TagScheduleFormData {
  brands: Array<string>;
  regions: Array<string>;
  activeStartDate: string | null;
  activeEndDate: string | null;
  travelStartDate: string | null;
  travelEndDate: string | null;
}

interface TagScheduleFormProps {
  control: Control<TagScheduleFormData>;
}

export default function TagScheduleForm({ control }: TagScheduleFormProps) {
  const activeStartDate = useWatch({ control, name: 'activeStartDate' });
  const travelStartDate = useWatch({ control, name: 'travelStartDate' });

  const regionOptions = useMemo(() => {
    return getRegionNamesAndCode().map((region) => ({
      name: region.name,
      code: region.code,
    }));
  }, []);

  const allRegionCodes = useMemo(() => {
    return regionOptions.map((region) => region.code);
  }, [regionOptions]);

  const {
    field: brandsField,
    fieldState: { error: brandsError },
  } = useController({
    control,
    name: 'brands',
  });

  const {
    field: regionsField,
    fieldState: { error: regionsError },
  } = useController({
    control,
    name: 'regions',
    rules: { required: 'Please select at least one region' },
  });

  const isAllRegionsSelected = regionsField.value.length === allRegionCodes.length;

  const handleRegionsChange = (selectedValues: Array<string>) => {
    if (selectedValues.includes(SELECT_ALL_VALUE)) {
      if (isAllRegionsSelected) {
        regionsField.onChange([]);
      } else {
        regionsField.onChange(allRegionCodes);
      }
    } else {
      regionsField.onChange(selectedValues);
    }
  };

  return (
    <Stack spacing={2} mt={1}>
      <FormControl fullWidth error={!!brandsError}>
        <InputLabel>Brands</InputLabel>
        <Select
          multiple
          value={['luxuryescapes']}
          input={<OutlinedInput label="Brands" />}
          renderValue={(selected) => (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          disabled
        >
          {Object.entries(tenants).map(([key, tenant]) => (
            <MenuItem key={key} value={tenant.brand}>
              <Checkbox checked={brandsField.value.includes(tenant.brand)} />
              <ListItemText primary={tenant.title} />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{brandsError?.message}</FormHelperText>
      </FormControl>
      <FormControl fullWidth error={!!regionsError} required>
        <InputLabel>Regions</InputLabel>
        <Select
          multiple
          value={regionsField.value}
          onChange={(e) => handleRegionsChange(e.target.value as Array<string>)}
          onBlur={regionsField.onBlur}
          input={<OutlinedInput label="Regions" />}
          renderValue={(selected) => (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {selected.length === allRegionCodes.length ? (
                <Chip label="All Regions" size="small" />
              ) : (
                selected.map((code) => {
                  const region = regionOptions.find((r) => r.code === code);
                  return <Chip key={code} label={region?.name || code} size="small" />;
                })
              )}
            </Box>
          )}
        >
          <MenuItem value={SELECT_ALL_VALUE}>
            <Checkbox
              checked={isAllRegionsSelected}
              indeterminate={regionsField.value.length > 0 && !isAllRegionsSelected}
            />
            <ListItemText primary="Select All" />
          </MenuItem>
          {regionOptions.map((region) => (
            <MenuItem key={region.code} value={region.code}>
              <Checkbox checked={regionsField.value.includes(region.code)} />
              <ListItemText primary={region.name} />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{regionsError?.message}</FormHelperText>
      </FormControl>
      <Controller
        name="activeStartDate"
        control={control}
        rules={{ required: 'Please select active period start date' }}
        render={({ field, fieldState: { error } }) => {
          const dateValue = field.value ? dayjs(field.value) : null;
          return (
            <DatePicker
              label="Active Period Start"
              value={dateValue}
              onChange={(date) => field.onChange(date ? formatDateLongISO(date.toDate()) : null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  onBlur: field.onBlur,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name="activeEndDate"
        control={control}
        rules={{ required: 'Please select active period end date' }}
        render={({ field, fieldState: { error } }) => {
          const dateValue = field.value ? dayjs(field.value) : null;
          const minDate = activeStartDate ? dayjs(activeStartDate) : undefined;
          return (
            <DatePicker
              label="Active Period End"
              value={dateValue}
              onChange={(date) => field.onChange(date ? formatDateLongISO(date.toDate()) : null)}
              minDate={minDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  onBlur: field.onBlur,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name="travelStartDate"
        control={control}
        render={({ field }) => {
          const dateValue = field.value ? dayjs(field.value) : null;
          return (
            <DatePicker
              label="Travel Period Start"
              value={dateValue}
              onChange={(date) => field.onChange(date ? formatDateLongISO(date.toDate()) : null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  onBlur: field.onBlur,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name="travelEndDate"
        control={control}
        render={({ field }) => {
          const dateValue = field.value ? dayjs(field.value) : null;
          const minDate = travelStartDate ? dayjs(travelStartDate) : undefined;
          return (
            <DatePicker
              label="Travel Period End"
              value={dateValue}
              onChange={(date) => field.onChange(date ? formatDateLongISO(date.toDate()) : null)}
              minDate={minDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  onBlur: field.onBlur,
                },
              }}
            />
          );
        }}
      />
    </Stack>
  );
}
